/**
 * Design tokens for the whole client (docs/DESIGN.md).
 *
 * Written first for the copilot. FloatingAssistant.tsx hardcoded hex values and pixel sizes inline
 * across dozens of className strings — an arbitrary text size and a raw brand hex per element —
 * which is how the type scale ended up running from 8px to 12px with no rule behind it, on a panel
 * showing money.
 * Tokens exist so a size or a colour is chosen once and named, not retyped per element.
 *
 * Moved here from components/copilot/ on 2026-09-10, when the rest of the app adopted them: every
 * page had drifted the same way the copilot once had.
 *
 * The palette is the app's existing brand (#085489 / #0A6AAC / #E6F0F7), not a new one.
 */

/**
 * Type scale. The floor is 12px and it is deliberate: the old panel rendered financial figures at
 * 9px, which is unreadable at arm's length and indefensible on a tool people use all day.
 *
 * `tabular-nums` rides along with every numeric role so digits line up down a column — proportional
 * digits make a column of money impossible to compare at a glance.
 */
export const TYPE = {
  /** The answer's headline. */
  title: 'text-[19px] leading-tight font-semibold',
  /** A section within an answer. */
  heading: 'text-[15px] leading-snug font-semibold',
  /** Running prose. 1.55 line-height — the brief's floor is 1.5. */
  body: 'text-[14px] leading-[1.55]',
  /** Inside a table cell. */
  cell: 'text-[13px] leading-snug',
  /** Labels, captions, timestamps. Never a figure the user must read. */
  meta: 'text-xs leading-snug',
  /** The one number an answer is about. */
  metric: 'text-[30px] leading-none font-bold tabular-nums tracking-tight',
  /** A number in a table or a key-value row. */
  figure: 'tabular-nums',
} as const;

/**
 * Semantic colour. Meaning first, hue second — the label always carries the meaning so the
 * component still works in monochrome and for a colour-blind reader.
 */
export const TONE = {
  positive: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  attention: 'bg-amber-50 text-amber-800 border-amber-200',
  negative: 'bg-red-50 text-red-800 border-red-200',
  /** Overdue is a negative that has to shout louder than the others. */
  overdue: 'bg-red-100 text-red-900 border-red-300 font-semibold',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
} as const;

export type Tone = keyof typeof TONE;

/** Brand surfaces, from the existing palette. */
export const SURFACE = {
  panel: 'bg-white',
  sunk: 'bg-slate-50',
  line: 'border-slate-200',
  brand: 'text-primary-600',
  brandBg: 'bg-primary-50',
  brandBorder: 'border-primary-500',
} as const;

/**
 * Prose measure. An expanded panel can be 900px wide; running text set that wide is genuinely
 * harder to read than the 420px panel was, so the measure is capped independently of the container.
 */
export const MEASURE = 'max-w-[68ch]';

/** Spacing and radius, so a card and a table agree on their edges. */
export const BOX = {
  radius: 'rounded-lg',
  radiusSm: 'rounded-md',
  padding: 'px-3 py-2.5',
  gap: 'gap-2',
} as const;

/**
 * The one tab / segmented-control look (UI-11): a grey track, the chosen option in the brand blue with white
 * text, 6px corners. SegmentedControl draws it, and the tab bars that keep real ARIA tabs (reports, bug
 * detail, item groups) reuse the same classes, so every tab bar in the app reads the same.
 */
export const SEGMENT_TRACK = 'inline-flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1';
export const segmentClass = (active: boolean) =>
  [
    'inline-flex h-7 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
    active ? 'bg-primary-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900',
    FOCUS,
  ].join(' ');

/** Focus ring, applied to everything interactive. Keyboard users are not optional. */
export const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1';

/**
 * A text input, textarea or select in a form. The caller adds a height and the border colour,
 * because the border is the one thing a validation error changes. Promoted from the Vendor and
 * Client forms, which each kept an identical copy, when the user and role forms joined them.
 */
export const FIELD =
  'w-full rounded-md border px-3 text-sm text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';
